import { useState } from 'react';
import { Car, Plus, Download, Loader2, Search } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useCompanyCars } from '../hooks/useCompanyCars';
import FleetTab from '../components/company-cars/FleetTab';
import ReferenceDataTab from '../components/company-cars/ReferenceDataTab';
import CarFormModal from '../components/company-cars/CarFormModal';
import CarDocumentsModal from '../components/company-cars/CarDocumentsModal';
import type { CompanyCar, CreateCarData, UpdateCarData } from '../types/companyCars';

type TabId = 'fleet' | 'reference';

export default function CompanyCarsPage() {
    const { canEdit } = usePagePermissions('/company-cars');
    const {
        cars, totalCars, brands, fuelTypes, leasingCompanies, insuranceCompanies,
        employees, companies, isLoading,
        companyFilter, setCompanyFilter, activeOnly, setActiveOnly,
        searchText, setSearchText,
        sortField, sortDir, handleSort,
        createCar, updateCar, deleteCar, exportCars, refreshCars,
        createBrand, updateBrand, createFuelType, updateFuelType,
        createLeasingCompany, updateLeasingCompany,
        createInsuranceCompany, updateInsuranceCompany,
    } = useCompanyCars();

    const [activeTab, setActiveTab] = useState<TabId>('fleet');
    const [showModal, setShowModal] = useState(false);
    const [editingCar, setEditingCar] = useState<CompanyCar | null>(null);
    const [exporting, setExporting] = useState(false);
    const [documentsCar, setDocumentsCar] = useState<CompanyCar | null>(null);

    const handleExport = async () => { setExporting(true); await exportCars(); setExporting(false); };

    const handleSave = async (data: CreateCarData & Pick<UpdateCarData, 'moveDocuments'>) => {
        const { moveDocuments, ...car } = data;
        if (editingCar) await updateCar(editingCar.Id, moveDocuments ? { ...car, moveDocuments } : car);
        else await createCar(car);
    };

    const handleEditCar = (car: CompanyCar) => { setEditingCar(car); setShowModal(true); };

    const activeCars = cars.filter(c => c.IsActive).length;

    if (isLoading) {
        return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Car className="w-6 h-6 text-blue-600" />
                    <h1 className="text-xl font-bold text-gray-800">Company Cars</h1>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <button onClick={() => { setEditingCar(null); setShowModal(true); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                            <Plus className="w-4 h-4" /> Add Car
                        </button>
                    )}
                    <button onClick={handleExport} disabled={exporting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40">
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Company:</label>
                    <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500">
                        <option value="">All</option>
                        {companies.map(c => (
                            <option key={c.CompanyCode} value={c.CompanyCode}>{c.CompanyName}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Status:</label>
                    <select value={activeOnly ? 'active' : 'all'} onChange={e => setActiveOnly(e.target.value === 'active')}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500">
                        <option value="active">Active only</option>
                        <option value="all">All</option>
                    </select>
                </div>
                <div className="flex items-center gap-2 flex-1 max-w-xs">
                    <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            placeholder="Search driver, brand, plate..."
                            className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>
                <span className="text-sm text-gray-400 ml-auto">
                    {activeCars} active of {totalCars} vehicles
                    {searchText && ` (${cars.length} shown)`}
                </span>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                    {([{ id: 'fleet' as TabId, label: 'Fleet' }, { id: 'reference' as TabId, label: 'Reference Data' }]).map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'fleet' && (
                <FleetTab
                    cars={cars}
                    companies={companies}
                    canEdit={canEdit}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={handleSort}
                    onDelete={deleteCar}
                    onEditCar={handleEditCar}
                    onOpenDocuments={setDocumentsCar}
                />
            )}

            {activeTab === 'reference' && (
                <ReferenceDataTab
                    brands={brands}
                    fuelTypes={fuelTypes}
                    leasingCompanies={leasingCompanies}
                    insuranceCompanies={insuranceCompanies}
                    canEdit={canEdit}
                    onCreateBrand={createBrand}
                    onUpdateBrand={updateBrand}
                    onCreateFuelType={createFuelType}
                    onUpdateFuelType={updateFuelType}
                    onCreateLeasingCompany={createLeasingCompany}
                    onUpdateLeasingCompany={updateLeasingCompany}
                    onCreateInsuranceCompany={createInsuranceCompany}
                    onUpdateInsuranceCompany={updateInsuranceCompany}
                />
            )}

            {documentsCar && (
                <CarDocumentsModal
                    car={documentsCar}
                    canEdit={canEdit}
                    onClose={() => setDocumentsCar(null)}
                    onChanged={() => { void refreshCars(); }}
                />
            )}

            {/* Modal */}
            {showModal && (
                <CarFormModal
                    car={editingCar}
                    brands={brands}
                    fuelTypes={fuelTypes}
                    leasingCompanies={leasingCompanies}
                    insuranceCompanies={insuranceCompanies}
                    employees={employees}
                    companies={companies}
                    onSave={handleSave}
                    onClose={() => { setShowModal(false); setEditingCar(null); }}
                    onCreateBrand={createBrand}
                    onCreateLeasingCompany={createLeasingCompany}
                    onCreateInsuranceCompany={createInsuranceCompany}
                />
            )}
        </div>
    );
}
